import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { translate } from '@fiverr-private/i18n-react';
import { useBreakpoints } from '@fiverr-private/hooks';
import { TimesButton } from '@fiverr-private/button';
import { HistoryIcon, SearchIcon } from '@fiverr-private/visuals';
import { Container } from '@fiverr-private/layout_components';
import SearchHighLighter from '../../../components/SearchHighLighter';
import { mapStateToProps, mapDispatchToProps } from '../connectSuggestionItem';
import {
    aiPillRowClass,
    aiPillRowSelectedClass,
    aiPillHistoryRowClass,
    aiPillRemoveButtonClass,
    aiPillRevealClass,
} from '../styles.ve.css';
import { getRevealDelay } from '../getRevealDelay';

const AiSuggestionRow = ({ id, index, value, isSelected, isHistory, shouldReveal, onClick, onRemove }) => {
    const breakpoints = useBreakpoints();
    // Locked at mount: re-renders of an already-visible row must not add or drop the reveal mid-animation.
    const [isRevealed] = useState(shouldReveal);
    const Icon = isHistory ? HistoryIcon : SearchIcon;

    const handleClick = (e) => {
        e.preventDefault();
        onClick(value);
    };

    const handleRemove = (e) => {
        // Removing unmounts this row before the click reaches `document.body`, where the
        // panel's outside-click check would see a detached target and close the panel.
        e.preventDefault();
        e.stopPropagation();
        onRemove();
    };

    return (
        <Container
            as="li"
            position="relative"
            className={classNames(aiPillRowClass, {
                [aiPillRevealClass]: isRevealed,
                [aiPillHistoryRowClass]: isHistory,
                selected: isSelected,
                [aiPillRowSelectedClass]: isSelected,
            })}
            style={isRevealed ? { animationDelay: getRevealDelay(index) } : undefined}
            display="flex"
            borderRadius="2xl"
            alignItems="center"
            paddingX={{ default: '0', md: '4' }}
            paddingY={{ default: '1', md: '2' }}
        >
            <Container
                as="button"
                type="button"
                display="flex"
                onClick={handleClick}
                data-id={id}
                flexGrow="1"
                minWidth="0"
                alignItems="center"
                gap="3"
            >
                <Container
                    as="span"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width={{ default: '28px', md: '36px' }}
                    height={{ default: '28px', md: '36px' }}
                    borderRadius="lg"
                    backgroundColor="grey_100"
                    flexShrink="0"
                >
                    <Icon color="grey_900" size="sm" />
                </Container>
                <Container
                    as="span"
                    minWidth="0"
                    color="grey_1200"
                    fontSize={{ default: 'b_sm', md: 'b_md' }}
                    textOverflow="ellipsis"
                    overflow="hidden"
                    whiteSpace="nowrap"
                >
                    <SearchHighLighter text={value} />
                </Container>
            </Container>
            {isHistory && (
                <TimesButton
                    className={aiPillRemoveButtonClass}
                    ariaLabel={translate('search_bar.ai_pill.remove_recent')}
                    size={breakpoints.md ? 'sm' : 'xs'}
                    shape="circle"
                    onClick={handleRemove}
                />
            )}
        </Container>
    );
};

AiSuggestionRow.propTypes = {
    id: PropTypes.string.isRequired,
    index: PropTypes.number,
    value: PropTypes.string,
    isSelected: PropTypes.bool,
    isHistory: PropTypes.bool,
    shouldReveal: PropTypes.bool,
    onClick: PropTypes.func,
    onRemove: PropTypes.func,
};

AiSuggestionRow.defaultProps = {
    index: 0,
    value: '',
    isSelected: false,
    isHistory: false,
    shouldReveal: true,
    onClick: () => true,
    onRemove: () => true,
};

export { AiSuggestionRow };

export default connect(mapStateToProps, mapDispatchToProps)(AiSuggestionRow);
